<script setup lang="ts">
import type {
    PrintTemplateCreateModel,
    PrintTemplateModel,
    PrintTemplateUpdateModel
} from '@/types/models/PrintTemplateModel'
import {
    BaseDialog,
    IconItem,
    SecondaryButton,
    SelectorField,
    StackLayout,
    FileInputField,
    StringInputField,
    TypographyItem
} from '@rfc/rfc-components-library'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { ETemplate, ETemplateLabels, ETemplateValues } from '../../../types/enum/TemplateEnum'

type Props = {
    open: boolean
    template?: PrintTemplateModel | null
}
const props = defineProps<Props>()
type Emits = {
    (e: 'close'): void
    (e: 'submit', data: PrintTemplateCreateModel | PrintTemplateUpdateModel): void
}
const emit = defineEmits<Emits>()

const { t } = useI18n()

const templateData = ref<PrintTemplateCreateModel>({
    name: '',
    type: ETemplate.RFID_CHIP
})

const dragOver = ref(false)
const fileInput = ref<HTMLInputElement | null>(null)

const edit = computed(() => !!props.template)
const title = computed(() =>
    edit.value ? t('templates.dialog.editTitle') : t('templates.dialog.createTitle')
)
const isDisabled = computed(
    () =>
        !templateData.value?.name?.trim() ||
        !templateData.value?.type ||
        (!edit.value && !templateData.value?.file)
)

const templateOptions = ETemplateValues.map(value => ({ label: t(ETemplateLabels[value]), value }))

watch(
    () => props.template,
    newTemplate => {
        if (newTemplate) {
            templateData.value = { ...(templateData.value ?? {}), ...(props.template ?? {}) }
        }
    }
)

const handleChange = (value: Partial<PrintTemplateCreateModel>) =>
    (templateData.value = { ...(templateData.value ?? {}), ...value })

const onDrop = (event: DragEvent) => {
    dragOver.value = false
    const files = event.dataTransfer?.files
    if (files?.length) templateData.value.file = files[0]
}

const handleSubmit = () => {
    if (
        isDisabled.value &&
        (!templateData.value ||
            !templateData.value?.name ||
            !templateData.value?.file ||
            !templateData.value?.type)
    )
        return
    return emit('submit', templateData.value)
}
</script>

<template>
    <BaseDialog
        :open="open"
        :title="title"
        :confirmLabel="edit ? $t('common.save') : $t('common.create')"
        :disabled="isDisabled"
        @close="$emit('close')"
        @confirm="handleSubmit">
        <template #content>
            <StackLayout direction="column" :gap="8" class="template-form">
                <StringInputField
                    :modelValue="templateData.name"
                    :label="$t('templates.form.name')"
                    @change="handleChange({ name: $event })" />

                <SelectorField
                    :modelValue="templateData.type"
                    :label="$t('templates.form.type')"
                    @change="handleChange({ type: $event })"
                    :options="templateOptions" />

                <FileInputField
                    ref="fileInput"
                    :modelValue="templateData.file"
                    accept=".nlbl"
                    @change="
                        handleChange({
                            file: Array.isArray($event) ? $event[0] : $event
                        })
                    " />
            </StackLayout>
        </template>
    </BaseDialog>
</template>

<style scoped lang="scss">
.template-form {
    min-width: 400px;
}

.file-dropzone {
    border: 2px dashed rgba(var(--v-border-color), 0.4);
    border-radius: 8px;
    cursor: pointer;
    transition: border-color 0.2s;

    &--over {
        border-color: rgb(var(--v-theme-primary));
        background-color: rgba(var(--v-theme-primary), 0.04);
    }

    &--filled {
        border-color: rgb(var(--v-theme-success));
    }
}
</style>
